import {
  AnalyticsActionConsentEvent,
  AnalyticsEventParam,
  ConsentContext,
  IntegrationType,
  PrivacyApi,
  PrivacyEvent,
} from '@accedo/one-plugin-sdk';
import { ErrorBoundary } from '@dynamic/ui';
import React, {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';

import { userService } from '#/client/api';
import { useFirstPluginConfig } from '#/plugins/hooks/useFirstPluginConfig';
import { ignoreError } from '#/service/utils';
import { useBootStore } from '#/store/boot/boot.store';
import { privacyActions } from '#/store/privacy/privacy.store';

import { pluginManager } from '../pluginManager';

const onPrivacyApiError = (...errorDetails: unknown[]) => {
  // I only see this path used when the plugin's injected script tags fail to load the 3rd party SDKs.
  // OneTrust forwards the `onerror` event from the script tag (not an error), SourcePoint just have empty rejects.
  // Either way, we have no idea what the actual error is.
  // I decided not to report this to Sentry, because it's not useful.
  console.error('Privacy plugin error', ...errorDetails);
};

export const PrivacyManager = () => {
  const { isPluginManagerReady } = useBootStore();
  const loaded = useRef<boolean>(false);

  useEffect(() => {
    return () => {
      privacyActions.reset();
    };
  }, []);

  // Get the first enabled ad feature config
  const enabledPrivacyConfig = useFirstPluginConfig(IntegrationType.CONSENT);

  const handleSaveRef: React.Ref<PrivacyApi> = useCallback(
    (apiRef) => {
      if (!enabledPrivacyConfig || loaded.current || !apiRef) {
        return;
      }
      privacyActions.save({
        apiRef,
        properties: enabledPrivacyConfig.properties,
      });
      loaded.current = true;
      privacyActions.addEventListener(
        PrivacyEvent.Loaded,
        (data: ConsentContext) => {
          privacyActions.saveConsents(data);
          privacyActions.toggleFloatingButton(
            !enabledPrivacyConfig.properties.disableFloatingButton
          );
        }
      );
      privacyActions.addEventListener(PrivacyEvent.Error, onPrivacyApiError);
      privacyActions.addEventListener(
        PrivacyEvent.ConsentUpdated,
        (data: ConsentContext) => {
          privacyActions.saveConsents(data);

          // Only notify core if we are handling gdpr consents
          if (data?.gdpr) {
            userService.refresh().catch(ignoreError);
          }
          document.dispatchEvent(
            new AnalyticsActionConsentEvent({
              [AnalyticsEventParam.Consent]: data,
            })
          );
        }
      );
    },
    [enabledPrivacyConfig]
  );

  const PrivacyComponent = useMemo(
    () =>
      pluginManager.getPluginComponent<{
        apiRef?: React.Ref<PrivacyApi>;
      }>({
        type: IntegrationType.CONSENT,
        id: enabledPrivacyConfig?.key,
        optional: true,
      }),
    [enabledPrivacyConfig?.key]
  );

  if (!enabledPrivacyConfig || !isPluginManagerReady) {
    return null;
  }

  return (
    <ErrorBoundary
      key={enabledPrivacyConfig.key}
      onError={PrivacyComponent.onError}
    >
      <Suspense fallback={null}>
        <PrivacyComponent apiRef={handleSaveRef} />
      </Suspense>
    </ErrorBoundary>
  );
};
